import { useRouter } from 'next/router';
import URL from '@/creditflowresearch/Router/urlConstants';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import {
  useCorporateMarketFilters,
  useEmMarketArticlesFilters,
  useCorporateFilters,
} from '@/creditflowresearch/application/PageFilters/hooks';
import getPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getPrefixForTitle';
import getMarketPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getMarketPrefixForTitle';
import getProductPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getProductPrefixForTitle';
import { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces/Column';
import { IFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import getAbsColumns from '@/creditflowresearch/ui/components/creditFlow/articles/columns';
import getCorporateMarketColumns from '@/creditflowresearch/ui/components/creditFlow/articles/corporateMarketColumns';
import getEmergingMarketColumns from '@/creditflowresearch/ui/components/creditFlow/articles/emergingMarketColumns';
import { useUserTimeZoneContext } from '@/creditflowresearch/application/User/UserTimeZone/UserTimeZoneContext';
import Market from '@/creditflowresearch/domain/Market';
import Product from '@/creditflowresearch/domain/Product';
import AbsProduct from '@/creditflowresearch/domain/Product/entities/AbsProduct';
import Region from '@/creditflowresearch/domain/Region';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { useFeatureToggleContext } from '@framework/ui/Organisms/FeatureToggle/Context';
import { HIDE_POSTS_FILTERS } from '@creditflowresearch/config/featureToggle';

import marketColors from '@/creditflowresearch/ui/common/styles/marketColors';

export interface IArticlesConfig {
  columns: IColumn[],
  headerFilters: IFilterSet[],
  titlePrefix: string,
  title?: string,
  panelBackground: string,
  tabsBasePath: string,
  showFiltersButton: boolean,
  needsSegmentValidation?: boolean,
}

export const useRegionBasedArticlesConfig = (): IArticlesConfig => {
  const { query } = useRouter();
  const { regions, products } = query;
  const { regionFilters } = usePageFiltersContext();
  const corporateFilters = useCorporateFilters();
  const { timeZone } = useUserTimeZoneContext();

  const regionIds = Region.getIds(regions);
  const productIds = Product.getIds(products);
  const panelBackground = useProductGradient();

  const isAbsProduct = productIds.some((id) => Product.isOfType(id, AbsProduct.TYPE));

  const headerFilters = isAbsProduct
    ? [
        {
          key: 'regions',
          items: regionFilters,
        },
      ]
    : corporateFilters;

  return {
    columns: getAbsColumns(timeZone),
    headerFilters,
    titlePrefix: getPrefixForTitle(regionIds, productIds),
    panelBackground,
    tabsBasePath: '',
    showFiltersButton: true,
  };
};

export const useCorporateMarketArticlesConfig = (): IArticlesConfig => {
  const { query } = useRouter();
  const { markets, products } = query;
  const corporateMarketFilters = useCorporateMarketFilters();
  const { timeZone } = useUserTimeZoneContext();

  const marketIds = Market.getIds(markets);
  const productIds = Product.getIds(products);

  const marketPrefix = getMarketPrefixForTitle(marketIds);
  const productPrefix = getProductPrefixForTitle(productIds);
  const panelBackground = useProductGradient();

  const { getFeatureByName } = useFeatureToggleContext();
  const { isEnabled: showPostsFilters } = getFeatureByName(HIDE_POSTS_FILTERS);

  return {
    columns: getCorporateMarketColumns(timeZone),
    headerFilters: corporateMarketFilters,
    titlePrefix: marketPrefix + productPrefix,
    panelBackground,
    tabsBasePath: '',
    showFiltersButton: showPostsFilters,
    needsSegmentValidation: true,
  };
};

export const useEmergingMarketArticlesConfig = (): IArticlesConfig => {
  const emMarketFilters = useEmMarketArticlesFilters();
  const panelBackground = marketColors.gradientMarketEm;
  const { timeZone } = useUserTimeZoneContext();

  const { getFeatureByName } = useFeatureToggleContext();
  const { isEnabled: showPostsFilters } = getFeatureByName(HIDE_POSTS_FILTERS);

  return {
    columns: getEmergingMarketColumns(timeZone),
    headerFilters: emMarketFilters,
    titlePrefix: '',
    title: 'EM Commentary & Deal Flow',
    panelBackground,
    tabsBasePath: URL.MARKET_OVERVIEW_EM,
    showFiltersButton: showPostsFilters,
  };
};
